import $ from "jquery";
import { useState, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import classNames from "classnames";
import Radio from "backbone.radio";
import { findKey, startsWith } from "lodash-es";
import styled from "styled-components";
import { rgba } from "polished";

import strings from "common/strings";

import { updateStickyItemsWidth } from "common/utils";

import { supportedEnvs } from "common/actions/settings";

import { pazienHistory } from "common/components/pazienHistory";
import { ensureErrorCallback } from "common/components/error";

import regularLogo from "img/logo@2x.png";
import collapsedLogo from "img/logo-icon@2x.png";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldCheck } from "extensions/fa5-icons/faShieldCheck";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import {
    faAngleDown,
    faBars,
    faAngleRight,
    faAngleLeft,
    faKey,
    faTachometerAlt,
    faStopwatch,
    faCctv,
    faUsers,
    faShoppingBasket,
    faEnvelope,
    faSearch,
    faExchange,
    faBell
} from "@fortawesome/pro-regular-svg-icons";
import {
    faClipboardList,
    faTools,
    faBook
} from "@fortawesome/pro-light-svg-icons";
import { faFilesToDownload } from "extensions/fa5-icons/faFilesToDownload";
import { faReports } from "extensions/fa5-icons/faReports";
import { faBenchmark } from "extensions/fa5-icons/faBenchmark";
import { faAnalytics } from "extensions/fa5-icons/faAnalytics";

import { AppVersionRedux } from "./AppVersion";
import "./sidebar.scss";

export function Sidebar(props) {
    const admin = pazienHistory.isAdminApp();

    const [collapsed, setCollapsed] = useState(false);
    const [collapseSource, setCollapseSource] = useState(null);
    const [prevCollapsed, setPrevCollapsed] = useState(null);

    const superuser = useSelector((state) => state.common.user.superuser);
    const currentRoute = useSelector((state) => state.common.route);
    const user_permissions = useSelector((state) => state.permissions.user_permissions, shallowEqual);
    const config = useSelector((state) => state.config, shallowEqual);
    const userStartPage = useSelector((state) => state.user_merchant_settings.start_page);

    const getLogoImage = () => {
        if (collapsed) return collapsedLogo;
        return regularLogo;
    };

    const getTopPages = () => {
        if (admin) {
            return [{
                route: "administrator/credentials",
                text: "Credentials",
                hasAccess: true,
                iconProps: {
                    icon: faKey,
                    inverse: true
                }
            }, {
                route: "administrator/dashboard",
                text: "Prod dashboard",
                hasAccess: true,
                iconProps: {
                    icon: faTachometerAlt,
                    inverse: true
                }
            }, {
                route: "administrator/requests",
                text: "Requests",
                hasAccess: true,
                iconProps: {
                    icon: faStopwatch,
                    inverse: true
                }
            }, {
                route: "administrator/sessions",
                text: "Sessions",
                hasAccess: true,
                iconProps: {
                    icon: faCctv,
                    inverse: true
                }
            }, {
                route: "administrator/users",
                text: "Users",
                hasAccess: true,
                iconProps: {
                    icon: faUsers,
                    inverse: true
                }
            }, {
                route: "administrator/merchants",
                text: "Merchants",
                hasAccess: true,
                iconProps: {
                    icon: faShoppingBasket,
                    inverse: true
                }
            },
            {
                route: "administrator/reports/",
                text: "Reports",
                hasAccess: true,
                iconProps: {
                    icon: faClipboardList,
                    inverse: true
                },
                isMenuItem: true,
                items: [{
                    route: "administrator/reports/prod_support__merchant_configurations/",
                    text: "Merchant configs",
                    hasAccess: true
                }, {
                    route: "administrator/reports/prod_support__assigned_op_incidents/",
                    text: "Assigned incidents",
                    hasAccess: true
                }, {
                    route: "administrator/reports/prod_support__unassigned_op_incidents/",
                    text: "Unassigned incidents",
                    hasAccess: true
                }, {
                    route: "administrator/reports/monitoring__stuck_files/",
                    text: "Stuck files",
                    hasAccess: true
                }]
            }, {
                route: "administrator/email",
                text: "Email analytics",
                hasAccess: superuser,
                iconProps: {
                    icon: faEnvelope,
                    inverse: true
                }
            }, {
                route: "administrator/appregistry",
                text: "Apps/Servers",
                hasAccess: superuser,
                iconProps: {
                    icon: faClipboardList,
                    inverse: true
                }
            }, {
                route: "administrator/downloads",
                text: "Downloads",
                hasAccess: true,
                iconProps: {
                    icon: faFilesToDownload,
                    inverse: true
                }
            }];
        }

        return [{
            route: "dashboard",
            text: strings.page_dashboard,
            hasAccess: () => true,
            iconProps: {
                icon: faTachometerAlt,
                inverse: true
            }
        }, {
            route: "search",
            text: strings.page_search,
            hasAccess: user_permissions.has_transaction_details,
            iconProps: {
                icon: faSearch,
                inverse: true
            }
        }, {
            route: "reconciliation",
            text: strings.page_reconciliation,
            hasAccess: user_permissions.feature_reconciliation_view || user_permissions.feature_reconciliation_v3,
            iconProps: {
                icon: faExchange,
                inverse: true
            }
        },
        {
            route: "alerts2/inbox",
            text: "Alerts",
            hasAccess: user_permissions.feature_alerts,
            hide: !user_permissions.feature_alerts,
            iconProps: {
                icon: faBell,
                inverse: true
            }
        }, {
            route: "reports",
            text: "Reports",
            hasAccess: user_permissions.has_page_reports,
            lock: "package",
            iconProps: {
                icon: faReports,
                inverse: true
            }
        }, {
            route: "analytics/benchmarks",
            text: strings.page_benchmarks,
            hasAccess: user_permissions.has_benchmarks,
            hide: !user_permissions.has_benchmarks,
            lock: "package",
            iconProps: {
                icon: faBenchmark,
                inverse: true
            }
        }, {
            route: "",
            text: strings.page_analytics,
            hasAccess: user_permissions.has_page_analytics,
            iconProps: {
                icon: faAnalytics,
                inverse: true
            },
            expanded: true,
            items: [{
                route: "analytics/authorizations",
                text: strings.page_authorizations,
                hasAccess: user_permissions.data_access_authorizations,
                icon: "pz-icon-piechart",
                color: "blue-mid"
            }, {
                route: "analytics/settlements",
                text: strings.page_sales_refunds,
                hasAccess: user_permissions.data_access_sales_refunds,
                icon: "pz-icon-piechart",
                color: "aqua"
            }, {
                route: "analytics/chargebacks",
                text: strings.page_chargebacks,
                hasAccess: user_permissions.data_access_chargebacks,
                icon: "pz-icon-piechart",
                color: "purple-mid"
            }, {
                route: "analytics/fees",
                text: strings.page_fees,
                hasAccess: user_permissions.data_access_fees,
                icon: "pz-icon-piechart",
                color: "orange-mid"
            }, {
                route: "analytics/cashflow",
                text: strings.page_cashflow,
                hasAccess: user_permissions.has_page_cashflow,
                icon: "pz-icon-piechart",
                color: "green-mid"
            }]
        }, {
            route: "",
            text: strings.page_fraud,
            hasAccess: user_permissions.has_fraud,
            hide: !user_permissions.has_fraud && !user_permissions.feature_psd2_new && !user_permissions.feature_3ds_flex && !user_permissions.feature_fraudsight,
            iconProps: {
                icon: faShieldCheck
            },
            expanded: true,
            items: [
                {
                    route: "analytics/all_fraud",
                    text: strings.page_allfraud,
                    hide: !user_permissions.has_fraud,
                    hasAccess: user_permissions.has_fraud,
                    color: "blue-mid"
                },
                {
                    route: "analytics/3ds",
                    text: "3DS authentication",
                    hide: !user_permissions.feature_3ds_flex,
                    hasAccess: user_permissions.feature_3ds_flex,
                    color: "green-mid"
                },
                {
                    route: "analytics/fraudsight",
                    text: "Risk monitoring",
                    hide: !user_permissions.feature_fraudsight,
                    hasAccess: user_permissions.feature_fraudsight,
                    color: "green-mid"
                },
                {
                    route: "analytics/psd2_fraud",
                    text: strings.page_psd2,
                    hasAccess: user_permissions.feature_psd2_new,
                    hide: !user_permissions.feature_psd2_new,
                    color: "aqua"
                }
            ]
        }, {
            route: "downloads",
            text: strings.page_downloads,
            hasAccess: user_permissions.feature_export,
            iconProps: {
                icon: faFilesToDownload,
                inverse: true
            }
        }, {
            route: "dev/settings",
            text: "Superuser",
            hasAccess: user_permissions.has_page_development,
            hide: !user_permissions.has_page_development,
            iconProps: {
                icon: faTools,
                inverse: true
            },
            expanded: true,
            items: [{
                route: "dev/tools",
                text: "Tools",
                hasAccess: user_permissions.has_page_development,
                color: "aqua"
            }, {
                route: "dev/settings",
                text: "Advanced settings",
                hasAccess: user_permissions.has_page_development,
                color: "blue-mid"
            }]
        }];
    };

    const getBottomPages = () => {
        if (admin) return [];

        return [
            {
                route: "support",
                text: "Support",
                hasAccess: true,
                iconProps: {
                    icon: faBook,
                    inverse: true
                }
            }
        ];
    };

    const expandSidebar = (source, hover = false) => (e) => {
        if (e) {
            e.preventDefault();
        }

        if (hover) {
            document.querySelector("#sidebar-wrapper").classList.add("hoverExpanded");
        } else {
            document.querySelector("#sidebar-wrapper").classList.remove("hoverExpanded");
            document.getElementById("wrapper").classList.remove("collapsed");
            document.getElementById("wrapper").classList.add("expanded");

            if (source === "user") {
                Sidebar.reflowUI();
            }
        }

        setPrevCollapsed(collapsed);
        setCollapsed(false);
        setCollapseSource(source || collapseSource);
    };

    const autoExpandSidebar = (e) => {
        e.preventDefault();
        if (collapsed) {
            expandSidebar(collapseSource, true)(e);
        }
    };

    const autoCollapseSidebar = (e) => {
        e.preventDefault();
        if (prevCollapsed) {
            collapseSidebar(collapseSource, true)(e);
        }
    };

    const collapseSidebar = (source, hover = false) => (e) => {
        if (e) {
            e.preventDefault();
        }

        document.querySelector("#sidebar-wrapper").classList.remove("hoverExpanded");
        document.getElementById("wrapper").classList.remove("expanded");
        document.getElementById("wrapper").classList.add("collapsed");

        setPrevCollapsed(collapsed);
        setCollapsed(true);
        setCollapseSource(source);
    };

    const navigate = ({ route, hasAccess, lock, permission }) => (e) => {
        e.preventDefault();
        if (!route) return;

        if (hasAccess) {
            pazienHistory.navigateWithParams(route, { trigger: true });
        } else {
            Radio.channel("main").request("disabled:permission", permission, lock === "package");
        }
    };

    const goHome = (e) => {
        e.preventDefault();
        pazienHistory.navigateWithParams(userStartPage || config.defaultRouteMerchant, { trigger: true });
    };

    const autoSideBar = () => {
        if (collapseSource !== "user" && config.mediaQueryBreakpoint) {
            if (config.mediaQueryBreakpoint === "bp-large") {
                expandSidebar("auto")();
            } else {
                collapseSidebar("auto")();
            }
        }
    };

    const isActivePage = (page) => {
        if (currentRoute.startsWith("reports/")) {
            const reportRoute = currentRoute.replace("reports", "analytics");
            return page.route && reportRoute && startsWith(reportRoute, page.route);
        }
        return page.route && startsWith(currentRoute, page.route);
    };

    const renderPages = (pages) => {
        const lockIcon = (key) => (
            <span key={key} className="fa-layers fa-fw lock-icon status-icon">
                <FontAwesomeIcon className="outer-icon" icon={faLock}/>
                <FontAwesomeIcon transform="shrink-4" icon={faLock}/>
            </span>
        );

        return pages.map((page, index) => {
            if (page.hide) return null;

            const hasSubmenu = Array.isArray(page.items) && page.items.length;
            return (
                <div key={`page_${page.route || page.text}`} className={classNames("page-link")}>
                    { !page.hasAccess && lockIcon(page.route || page.text) }
                    <a onClick={navigate(page)} className={classNames({ "submenu-label": hasSubmenu && !page.isMenuItem, active: isActivePage(page) })} href={`/merchant/${pazienHistory.addParamsToFragment(page.route)}`}>
                        <span><FontAwesomeIcon className="page-icon" {...page.iconProps}/></span>
                        {collapsed && hasSubmenu && <span className="angle-down-icon"><FontAwesomeIcon icon={faAngleDown}/></span>}
                        <span className="page-name">{page.text}</span>
                    </a>

                    {!collapsed && hasSubmenu && (
                        <div className="submenu">
                            {page.items.map((subpage) => {
                                if (subpage.hide) return null;

                                return (
                                    <div key={`subpage_${subpage.route}`} data-active-color={subpage.color} className={classNames({ subpage: true, unlocked: subpage.hasAccess, locked: !subpage.hasAccess })}>
                                        {!subpage.hasAccess && lockIcon(`lock_icon_${subpage.route}`)}
                                        <a className={classNames({ active: isActivePage(subpage) })} onClick={navigate(subpage)} href={`/merchant/${pazienHistory.addParamsToFragment(subpage.route)}`}>{subpage.text}</a>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        });
    };

    useEffect(() => { Sidebar.reflowUI(); }, [collapsed]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { autoSideBar(); }, [config.mediaQueryBreakpoint]);

    return (
        <div className={classNames("sidebar-react", { collapsed })} onMouseEnter={autoExpandSidebar} onMouseLeave={autoCollapseSidebar}>
            <div className="top">
                <PazienLogo className="logo" env={findKey(supportedEnvs, (v) => v === config.api_host)} onClick={goHome} href={`/${admin ? "merchant/administrator/credentials" : "merchant/dashboard"}`}>
                    <img src={getLogoImage()} alt="Pazien" />
                </PazienLogo>
                <div className={classNames("pages", { collapsed })}>
                    {renderPages(getTopPages())}
                </div>
            </div>
            <div className="bottom">
                <div className="pages">
                    {renderPages(getBottomPages())}
                </div>
                {superuser && !collapsed && <AppVersionRedux className="app-version" /> }
                {collapsed || prevCollapsed
                    ? (
                        <div key="collapsed-control-expand" className="toggleWidth expandBar" onClick={expandSidebar("user")}>
                            <FontAwesomeIcon className="toggle-icon" icon={faBars}/>
                            <FontAwesomeIcon className="toggle-icon" icon={faAngleRight}/>
                            {!collapsed && <span className="collapse-label">EXPAND MENU</span>}
                        </div>
                    )
                    : (
                        <div key="collapsed-control-collapse" className="toggleWidth collapseBar" onClick={collapseSidebar("user")}>
                            <FontAwesomeIcon className="toggle-icon" icon={faAngleLeft}/>
                            <FontAwesomeIcon className="toggle-icon" icon={faBars}/>
                            <span className="collapse-label">COLLAPSE MENU</span>
                        </div>
                    )}
            </div>
        </div>
    );
}

Sidebar.reflowUI = () => {
    setTimeout(async() => {
        $(window).trigger("sidebar:updated");
        updateStickyItemsWidth();

        try {
            const { Highcharts } = await import(
                /* webpackChunkName: "highcharts_lib" */
                "extensions/highcharts"
            );
            Highcharts.reflowAll();
        } catch (error) {
            ensureErrorCallback(error);
        }
    }, 201); // Wait until animation ends
};

export const PazienLogo = styled.a(({ env, theme }) => {
    const colorCode = {
        development: theme.colors["green-leaf"],
        localhost: theme.colors["green-leaf"],
        staging: theme.colors["blue-wcag-327"],
        staging2: theme.colors["blue-wcag-327"],
        staging3: theme.colors["blue-wcag-327"],
        production: theme.colors.red
    };

    return `
        background-color: ${colorCode[env]};
        height: 51px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 70px;
        }

        &:hover {
            background-color: ${rgba(colorCode[env], 0.8)};
        }
    `;
});
